<template>
  <div v-if="!isContainer" class="json-row">
    <span v-if="showLabel" :class="['json-key', { 'json-key--match': keyMatches }]">{{ labelDisplay }}</span>
    <span v-if="showLabel" class="json-colon">: </span>
    <span
      v-if="isImageUrl"
      class="json-string json-image-trigger"
      @mouseenter="showPreview"
      @mouseleave="hidePreview"
    >{{ display }}</span>
    <a
      v-else-if="isLink"
      :href="href"
      target="_blank"
      class="json-link"
    >{{ display }}</a>
    <button
      v-else-if="isSearchable"
      class="json-search-link"
      type="button"
      @click="searchByPartNo?.(data as string)"
    >{{ display }}</button>
    <span v-else :class="typeClass">{{ display }}</span>
    <Teleport v-if="isImageUrl" to="body">
      <div
        v-if="previewVisible"
        class="json-image-preview"
        :style="{ left: `${previewX}px`, top: `${previewY}px` }"
      >
        <img
          :src="(data as string)"
          loading="lazy"
          alt=""
        >
      </div>
    </Teleport>
  </div>

  <details
    v-else
    :class="['json-node', { 'json-node--exact': isExactMatch, 'json-node--empty': childCount === 0 }]"
    :open="effectiveOpen"
  >
    <summary class="json-summary">
      <span v-if="showLabel" :class="['json-key', { 'json-key--match': keyMatches }]">{{ labelDisplay }}</span>
      <span v-if="showLabel" class="json-colon">: </span>
      <span class="json-preview">{{ preview }}</span>
    </summary>
    <div class="json-children">
      <JsonTree
        v-for="[key, val] in entries"
        :key="key"
        :data="val"
        :label="key"
        :open="key === 'hits' || (open && childCount === 1)"
        :preview-fields="label === 'hits' ? ['partNo', 'name'] : undefined"
        :filter="filter"
        :base-url="baseUrl"
        :link-values="label === 'urls'"
        :search-values="label === 'partNumbers'"
        :image-values="imageValues || String(label).toLowerCase().includes('mediaset')"
        :highlight-part-no="highlightPartNo"
      />
    </div>
  </details>
</template>

<script setup lang="ts">
defineOptions({ name: 'JsonTree' });

const props = defineProps<{
  data: unknown;
  label?: string | number;
  open?: boolean;
  previewFields?: string[];
  filter?: string;
  baseUrl?: string;
  linkValues?: boolean;
  searchValues?: boolean;
  imageValues?: boolean;
  highlightPartNo?: string;
}>();

const searchByPartNo = inject<((partNo: string) => void) | null>('searchByPartNo', null);

function hasMatch(data: unknown, filter: string): boolean {
  if (!data || typeof data !== 'object') { return false; }
  for (const key of Object.keys(data as object)) {
    if (key.toLowerCase().startsWith(filter.toLowerCase())) { return true; }
    if (hasMatch((data as Record<string, unknown>)[key], filter)) { return true; }
  }
  return false;
}

const showLabel = computed(() => props.label !== undefined);
const isArray = computed(() => Array.isArray(props.data));
const isContainer = computed(() => props.data !== null && typeof props.data === 'object');

const keyMatches = computed(() =>
  !!props.filter && props.label !== undefined &&
  String(props.label).toLowerCase().startsWith(props.filter.toLowerCase())
);

const effectiveOpen = computed(() => {
  if (props.filter) { return keyMatches.value || hasMatch(props.data, props.filter); }
  return props.open ?? false;
});

const labelDisplay = computed(() => {
  if (typeof props.label === 'number') { return props.label; }
  return `"${props.label}"`;
});

const typeClass = computed(() => {
  if (props.data === null) { return 'json-null'; }
  if (typeof props.data === 'string') { return 'json-string'; }
  if (typeof props.data === 'number') { return 'json-number'; }
  if (typeof props.data === 'boolean') { return 'json-bool'; }
  return '';
});

const display = computed(() => {
  if (props.data === null) { return 'null'; }
  if (typeof props.data === 'string') { return `"${props.data}"`; }
  return String(props.data);
});

const isImageUrl = computed(() =>
  typeof props.data === 'string' &&
  (props.data as string).startsWith('http') &&
  (String(props.label).toLowerCase().includes('image') || props.imageValues)
);

const isExactMatch = computed(() =>
  !!props.highlightPartNo &&
  !Array.isArray(props.data) &&
  props.data !== null &&
  typeof props.data === 'object' &&
  (props.data as Record<string, unknown>).partNo === props.highlightPartNo
);

const isLink = computed(() =>
  !isImageUrl.value &&
  typeof props.data === 'string' && (props.label === 'url' || props.label === 'variantUrl' || props.linkValues)
);

const isSearchable = computed(() =>
  typeof props.data === 'string' && props.searchValues
);

const href = computed(() => {
  const val = props.data as string;
  if (val.startsWith('http')) { return val; }
  if (props.baseUrl) { return `${props.baseUrl}/${val.replace(/^\//, '')}`; }
  return val;
});

const previewVisible = ref(false);
const previewX = ref(0);
const previewY = ref(0);

const showPreview = (e: MouseEvent) => {
  previewX.value = e.clientX + 16;
  previewY.value = e.clientY + 16;
  previewVisible.value = true;
};

const hidePreview = () => { previewVisible.value = false; };

const childCount = computed(() => Object.keys(props.data as Record<string, unknown>).length);

const entries = computed<[string | number, unknown][]>(() => {
  if (!isContainer.value) { return []; }
  if (isArray.value) {
    return (props.data as unknown[]).map((val, i) => [i, val]);
  }
  const obj = props.data as Record<string, unknown>;
  return Object.keys(obj).sort().map(key => [key, obj[key]]);
});

const preview = computed(() => {
  if (props.previewFields?.length && !isArray.value) {
    const obj = props.data as Record<string, unknown>;
    const extra = props.previewFields.map(f => obj[f]).filter(Boolean).join('  ');
    return `{${childCount.value}}${extra ? `  ${extra}` : ''}`;
  }
  return isArray.value ? `[${childCount.value}]` : `{${childCount.value}}`;
});
</script>

<style scoped lang="postcss">
.json-row {
  display: block;
  line-height: 1.6;
}

.json-node {
  display: block;
}

.json-summary {
  display: block;
  cursor: pointer;
  line-height: 1.6;
  list-style: none;
  user-select: none;

  &::-webkit-details-marker {
    display: none;
  }

  &::before {
    content: '▶ ';
    font-size: 12px;
    opacity: 0.5;
    display: inline-block;
    width: 12px;
  }
}

details[open] > .json-summary::before {
  content: '▼ ';
}

.json-node--empty > .json-summary {
  cursor: default;
  pointer-events: none;

  &::before {
    content: '';
  }
}

.json-children {
  padding-left: 20px;
  border-left: 1px solid var(--border);
  margin-left: 5px;
}

.json-key { color: var(--json-key); }
.json-key--match { background: var(--filter-match-bg); color: var(--filter-match-color); padding: 0 2px; }
.json-colon { color: var(--color); opacity: 0.6; }
.json-preview { color: var(--color); opacity: 0.5; }

.json-node--exact > .json-summary {
  background: var(--exact-match-bg, rgba(0, 180, 80, 0.12));
  outline: 1px solid var(--exact-match-border, rgba(0, 180, 80, 0.4));
  margin-left: -4px;
  padding-left: 4px;
}

.json-image-trigger {
  cursor: zoom-in;
}

.json-search-link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  color: var(--color-link);
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  text-decoration-color: color-mix(in srgb, var(--color-link) 40%, transparent);

  &:hover {
    text-decoration-style: solid;
    text-decoration-color: var(--color-link);
  }
}

.json-link {
  color: var(--color-link);
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  text-decoration-color: color-mix(in srgb, var(--color-link) 40%, transparent);

  &:hover {
    text-decoration-style: solid;
    text-decoration-color: var(--color-link);
  }
}

.json-string { color: var(--json-string); }
.json-number { color: var(--json-number); }
.json-bool   { color: var(--json-bool); }
.json-null   { color: var(--json-null); }
</style>

<style lang="postcss">
.json-image-preview {
  position: fixed;
  z-index: 9999;
  background: #1e1e1e;
  border: 1px solid #444;
  border-radius: 4px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  padding: 4px;
  pointer-events: none;

  img {
    display: block;
    max-width: 280px;
    max-height: 280px;
    object-fit: contain;
  }
}
</style>
